import loadable from "@loadable/component";
import { tr } from "date-fns/locale";
import { motion } from "framer-motion";
import { PropsWithChildren, ReactNode, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import BidItemLabel from "@krea/customer-features/shared-components/bid-dashboards/commons/BidItemLabel";
import BidsListTableTD from "@krea/customer-features/shared-components/bid-dashboards/commons/BidsListTableTD";
import Button from "@krea/customer-features/shared-components/button";
import { useGetCachedCurrentLoanApplication } from "@krea/customer-features/store/current-loan-application/hooks";
import { ILenderCompany } from "@krea/customer-features/types/lenderCompany";
import {
  ILenderResponse,
  IPromotion,
} from "@krea/customer-features/types/lenderResponse";
import { LoanApplicationStates } from "@krea/customer-features/types/loanApplication";
import twMerge from "@krea/customer-features/utils/tailwind-utils";
import { PromotionTypeEnum } from "@krea/shared/types/common";

import { BidItemContext } from "./BidItem";
import { getPromotionsAsText } from "./utils";

const AppTooltip = loadable(
  () => import("@krea/customer-features/shared-components/tooltip"),
);

const motionContainer = {
  hidden: {
    opacity: 0,
    height: 0,
  },
  visible: {
    opacity: 1,
    height: "auto",
  },
};

interface IBidsListItemDesktop {
  isSelected: boolean;
  isRecommended: boolean;
  isGoodAlternative: boolean;
  showDetails: boolean;
  bidDetails: ReactNode;
  lenderCompany: ILenderCompany;
  lenderResponse: ILenderResponse;
  additionalList: ReactNode;
  detailsButtonText: string;
  isPaidOut?: boolean;
  isPaidOutPage?: boolean;
}

const BidsListItemDesktop = ({
  isSelected,
  isRecommended,
  isGoodAlternative,
  showDetails,
  children,
  bidDetails,
  lenderCompany,
  lenderResponse,
  additionalList,
  detailsButtonText,
  isPaidOut,
  isPaidOutPage,
}: PropsWithChildren<IBidsListItemDesktop>) => {
  const { t } = useTranslation();
  const colSpan = 10;
  const context = useContext(BidItemContext);

  let labelText,
    accentColor: "mint" | "primary" | undefined = undefined;
  const { data: loanApplication } = useGetCachedCurrentLoanApplication();
  const disableProceed =
    (loanApplication?.state ===
      LoanApplicationStates.PAYOUT_RECEIVED_BY_CUSTOMER &&
      !isSelected) ||
    isPaidOut;

  if (!isPaidOutPage) {
    if (isPaidOut) {
      labelText = t("dash.paidOutBidLabel");
      accentColor = "mint";
    } else if (isSelected) {
      labelText = t("dash.selectedBidLabel");
      accentColor = "mint";
    } else if (isRecommended) {
      labelText = t("dash.recommendedBidLabel");
      accentColor = "primary";
    } else if (isGoodAlternative) {
      labelText = t("dash.goodAlternativeBidLabel");
    }
  }

  return (
    <div
      className={twMerge(
        "tw-mb-[25px] tw-border tw-border-solid tw-rounded-md tw-border-gray-500",
        {
          "tw-border-primary": isRecommended && !isSelected && !isPaidOut,
          "tw-border-mint-500": isSelected || isPaidOut,
          "tw-bg-mint-100": isPaidOut,
        },
      )}
    >
      <tr className="tw-overflow-hidden">
        <BidsListTableTD
          colSpan={colSpan}
          className={twMerge("tw-relative tw-px-4")}
        >
          {labelText ? (
            <div
              className={twMerge(
                "tw-flex tw-align-middle tw-absolute tw--top-[1px] tw--left-[1px]",
              )}
            >
              <BidItemLabel text={labelText} accentColor={accentColor} />
            </div>
          ) : null}
        </BidsListTableTD>
      </tr>

      <div className="tw-mx-[10px] tw-mb-[8px]">
        <tr className="tw-flex tw-justify-between">
          <div className="tw-flex">
            <BidsListTableTD
              first={true}
              className="tw-mt-[13px] tw-h-[40px] tw-w-[110px] tw-p-0 tw-text-center"
            >
              {lenderCompany.image?.lowResolutionUrl ? (
                <img
                  src={lenderCompany.image.lowResolutionUrl}
                  alt={lenderCompany.name}
                  className={
                    "tw-mx-auto tw-mt-[5px] tw-h-[30px] tw-w-auto tw-object-contain tw-px-[5px]"
                  }
                />
              ) : (
                <AppTooltip
                  content={lenderCompany.name}
                  className="tw-text-truncate tw-w-full tw-shrink-0"
                >
                  <p className="tw-pt-[10px]">{lenderCompany.name}</p>
                </AppTooltip>
              )}
            </BidsListTableTD>

            <div>{children}</div>

            <BidsListTableTD>{additionalList}</BidsListTableTD>
          </div>

          <div className="tw-flex tw-pr-[15px]">
            <BidsListTableTD className="tw-flex">
              <Button
                className="tw-ml-0 tw-mr-3 tw-min-w-[120px] tw-shrink-0 tw-text-nowrap tw-p-0"
                variant="text"
                onClick={context.toggleDetails}
              >
                {detailsButtonText}
              </Button>

              {!isPaidOutPage && (
                <Button
                  variant={"contained"}
                  onClick={context.chooseBid}
                  disabled={!!disableProceed || context.isSelecting}
                >
                  {isSelected
                    ? t("dash.chooseBidButtonAfterSelected")
                    : t("dash.chooseBidButton")}
                </Button>
              )}
            </BidsListTableTD>
          </div>
        </tr>
        {lenderResponse.promotions && lenderResponse.promotions.length > 0 && (
          <tr>
            <div
              className={twMerge(
                "-tw-mx-[10px] tw--mb-4 tw-mt-1 tw-rounded-b-md tw-bg-mint-100 tw-p-2 tw-text-sm",
                {
                  "tw-rounded-b-none": showDetails,
                },
              )}
            >
              <div className="tw-px-1">
                <span className="tw-text-gray-600">
                  {t("dash.promotionTitle")}:{" "}
                </span>
                <span className="tw-font-bold">
                  {lenderResponse.promotions
                    .map((it) => {
                      return getPromotionsAsText(it);
                    })
                    .join(", ")}
                </span>
              </div>
            </div>
          </tr>
        )}
        <tr>
          <td
            className={twMerge("tw-m-0 tw-py-0 tw-px-0 tw-pb-2 tw-w-screen")}
            // colSpan={colSpan}
          >
            <motion.div
              variants={motionContainer}
              initial="hidden"
              animate={showDetails ? "visible" : "hidden"}
              transition={{
                default: { duration: 0.6, ease: "easeOut" },
              }}
              className={twMerge("tw-overflow-hidden tw-w-full")}
            >
              {bidDetails}
            </motion.div>
          </td>
        </tr>
      </div>
    </div>
  );
};

export default BidsListItemDesktop;
